import { useNavigate } from 'react-router-dom';

const BrochurePage = () => {
  const navigate = useNavigate();

  const handleDownload = () => {
    // Show an alert with instructions before opening print dialog
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const instructions = isMac 
      ? 'In the print dialog:\n1. Click "Show Details"\n2. Check "Print backgrounds"\n3. Select "Save as PDF" as destination\n4. Click Save'
      : 'In the print dialog:\n1. Enable "Background graphics"\n2. Select "Save as PDF" or "Microsoft Print to PDF"\n3. Click Save';
    
    alert(`📄 How to save this brochure:\n\n${instructions}`);
    
    // Small delay to let user read the instructions
    setTimeout(() => {
      window.print();
    }, 500);
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D]">
      {/* Navigation - Hidden when printing */}
      <div className="print:hidden fixed top-0 left-0 right-0 z-[9999] bg-[#0D0D0D]/95 backdrop-blur-sm border-b border-[#2F2F2F] py-4 px-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-white hover:text-[#00E600] transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="font-semibold">Back to Home</span>
            </button>
            
            <button
              onClick={handleDownload}
              className="px-8 py-3 bg-gradient-to-r from-[#00E600] to-[#36E0A8] text-[#0D0D0D] font-bold rounded-md hover:shadow-[0_0_20px_rgba(0,230,0,0.6)] transition-all duration-300 uppercase tracking-wide text-sm flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              <span>Download PDF</span>
            </button>
          </div>
          
          {/* Instructions */}
          <div className="bg-[#00E600]/10 border border-[#00E600]/30 rounded-md px-4 py-2">
            <p className="text-[#00E600] text-xs font-semibold flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              IMPORTANT: When downloading, enable "Background graphics" or "Print backgrounds" in your print settings to preserve colors and images.
            </p>
          </div>
        </div>
      </div>

      {/* Brochure Content */}
      <div className="max-w-[1200px] mx-auto">
        
        {/* Cover Page - Properly Centered */}
        <section className="min-h-screen h-screen flex items-center justify-center relative overflow-hidden page-break bg-[#0D0D0D] py-20">
          {/* Background Image */}
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1451187580459-43490279c0fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw5fHxuZXR3b3JrfGVufDB8fHx8MTczMzkyNTg5Nnww&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
          
          <div className="relative z-10 text-center px-8 w-full max-w-6xl flex flex-col items-center justify-center">
            {/* Glowing Logo - with top margin */}
            <div className="mb-20">
              <img 
                src="https://customer-assets.emergentagent.com/job_find-your-agent/artifacts/m94nk0ss_NYAXO.png" 
                alt="NYAXO Logo" 
                className="h-28 mx-auto"
                style={{
                  filter: 'drop-shadow(0 0 20px rgba(0, 230, 0, 0.5)) drop-shadow(0 0 40px rgba(0, 230, 0, 0.3))',
                  animation: 'glow 2s ease-in-out infinite alternate'
                }}
              />
            </div>

            {/* Slogan - Centered */}
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-10 uppercase tracking-wide leading-tight max-w-5xl">
              ENGINEERING THE FUTURE<br />
              <span className="text-[#00E600]" style={{
                textShadow: '0 0 20px rgba(0, 230, 0, 0.5), 0 0 40px rgba(0, 230, 0, 0.3)'
              }}>OF INTELLIGENCE</span>
            </h1>

            {/* Tagline - Centered */}
            <p className="text-xl text-[#B3B3B3] font-light tracking-wide leading-relaxed max-w-4xl">
              We design AI systems that transform corporate operations — from energy and infrastructure to SaaS.
            </p>
          </div>
        </section>

        {/* Facts & Figures Page */}
        <section className="min-h-screen h-screen py-16 px-10 page-break bg-[#0D0D0D] flex flex-col justify-center overflow-hidden">
          <div className="mb-10 text-center">
            <h2 className="text-4xl font-bold text-white mb-3 uppercase tracking-wide">
              Facts & <span className="text-[#00E600]">Figures</span>
            </h2>
            <div className="h-1 w-24 bg-[#00E600] mx-auto mb-4"></div>
            <p className="text-base text-[#B3B3B3] max-w-3xl mx-auto leading-relaxed">
              When Fortune 500 enterprises bet billions on execution, they choose NYAXO.
            </p>
          </div>

          {/* Key Statistics Grid */}
          <div className="grid grid-cols-3 gap-4 mb-8 max-w-6xl mx-auto">
            <div className="bg-[#1A1A1A] border border-[#00E600] rounded-lg p-5 text-center">
              <div className="text-4xl font-bold text-[#00E600] mb-2">$2B+</div>
              <div className="text-sm text-white font-semibold mb-1 uppercase tracking-wide">Projects Delivered</div>
              <div className="text-xs text-[#B3B3B3]">Enterprise projects on time & under budget</div>
            </div>

            <div className="bg-[#1A1A1A] border border-[#00E600] rounded-lg p-5 text-center">
              <div className="text-4xl font-bold text-[#00E600] mb-2">50M+</div>
              <div className="text-sm text-white font-semibold mb-1 uppercase tracking-wide">People Served</div>
              <div className="text-xs text-[#B3B3B3]">Citizens relying on our infrastructure daily</div>
            </div>

            <div className="bg-[#1A1A1A] border border-[#00E600] rounded-lg p-5 text-center">
              <div className="text-4xl font-bold text-[#00E600] mb-2">99.8%</div>
              <div className="text-sm text-white font-semibold mb-1 uppercase tracking-wide">Uptime Guarantee</div>
              <div className="text-xs text-[#B3B3B3]">Mission-critical system availability</div>
            </div>

            <div className="bg-[#1A1A1A] border border-[#00E600] rounded-lg p-5 text-center">
              <div className="text-4xl font-bold text-[#00E600] mb-2">94%</div>
              <div className="text-sm text-white font-semibold mb-1 uppercase tracking-wide">Project Success</div>
              <div className="text-xs text-[#B3B3B3]">On-time delivery vs 30% industry average</div>
            </div>

            <div className="bg-[#1A1A1A] border border-[#00E600] rounded-lg p-5 text-center">
              <div className="text-4xl font-bold text-[#00E600] mb-2">$500M+</div>
              <div className="text-sm text-white font-semibold mb-1 uppercase tracking-wide">Value Created</div>
              <div className="text-xs text-[#B3B3B3]">Transformation value for Global 2000</div>
            </div>

            <div className="bg-[#1A1A1A] border border-[#00E600] rounded-lg p-5 text-center">
              <div className="text-4xl font-bold text-[#00E600] mb-2">48hr</div>
              <div className="text-sm text-white font-semibold mb-1 uppercase tracking-wide">Early Detection</div>
              <div className="text-xs text-[#B3B3B3]">Infrastructure leak prediction advance</div>
            </div>
          </div>

          {/* Quote */}
          <div className="bg-[#1A1A1A] border-l-4 border-[#00E600] rounded-lg p-6 max-w-4xl mx-auto">
            <p className="text-lg text-white font-semibold italic mb-2 leading-relaxed">
              "Most companies implement AI. We interrogate it. We stress-test it. We engineer dominance."
            </p>
            <p className="text-[#00E600] text-sm font-semibold uppercase tracking-wide">— NYAXO Engineering Philosophy</p>
          </div>
        </section>

        {/* Introduction Page */}
        <section className="min-h-screen h-screen py-16 px-10 page-break bg-[#0D0D0D] flex flex-col justify-center overflow-hidden">
          <div className="mb-10">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              The <span className="text-[#00E600]">Provocateur</span>
            </h2>
            <div className="h-1 w-24 bg-[#00E600] mb-6"></div>
          </div>

          <div className="space-y-5 text-base text-[#B3B3B3] leading-relaxed max-w-4xl mb-10">
            <p className="text-lg text-white font-semibold">
              Most companies implement AI. We interrogate it. We stress-test it. We engineer dominance.
            </p>
            <p>
              NYAXO isn't a vendor—we're the architects behind systems that can't afford failure. When Fortune 500 
              enterprises bet billions on execution, when municipalities serve 50M+ citizens, when mission-critical 
              infrastructure operates 24/7/365—they choose NYAXO.
            </p>
            <p>
              Our AI predicts equipment failures 2-3 weeks before breakdowns. It detects infrastructure leaks 48 hours 
              before surface evidence. It optimizes billion-dollar project portfolios with 10,000+ variables in real-time.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-6">
            <div className="bg-[#1A1A1A] border-l-4 border-[#00E600] p-5">
              <h3 className="text-base font-bold text-white mb-2 uppercase tracking-wide">We Interrogate AI</h3>
              <p className="text-sm text-[#B3B3B3]">
                Adversarial validation before production stakes get real.
              </p>
            </div>
            <div className="bg-[#1A1A1A] border-l-4 border-[#00E600] p-5">
              <h3 className="text-base font-bold text-white mb-2 uppercase tracking-wide">We Stress-Test at Scale</h3>
              <p className="text-sm text-[#B3B3B3]">
                We engineer systems that compound under pressure.
              </p>
            </div>
            <div className="bg-[#1A1A1A] border-l-4 border-[#00E600] p-5">
              <h3 className="text-base font-bold text-white mb-2 uppercase tracking-wide">We Engineer Dominance</h3>
              <p className="text-sm text-[#B3B3B3]">
                Competitive advantages competitors can't copy.
              </p>
            </div>
          </div>
        </section>

        {/* AI Stress Testing Page */}
        <section className="min-h-screen py-24 px-12 page-break">
          <div className="relative h-64 mb-12 rounded-lg overflow-hidden">
            <div className="absolute inset-0 opacity-30" style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1660165458059-57cfb6cc87e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlfGVufDB8fHxibHVlfDE3NjEwNjM5NzN8MA&ixlib=rb-4.1.0&q=85)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}></div>
            <div className="relative z-10 h-full flex items-center px-8">
              <div>
                <h2 className="text-5xl font-bold text-white mb-4 uppercase tracking-wide">
                  AI <span className="text-[#00E600]">Stress Testing</span>
                </h2>
                <p className="text-xl text-[#B3B3B3]">Adversarial Validation Before Production Deployment</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-12">
            <div>
              <h3 className="text-2xl font-bold text-white mb-6 uppercase tracking-wide">The Problem</h3>
              <p className="text-[#B3B3B3] mb-4">
                70% of AI projects fail in production. Not from poor algorithms—but from untested edge cases, 
                adversarial scenarios, and failure modes no one anticipated. When your model crashes, millions 
                are lost before engineers even diagnose the cause.
              </p>
              <p className="text-[#00E600] font-semibold">
                → We test what breaks before it matters. Adversarial validation that exposes vulnerabilities 
                competitors won't discover until production failure costs them everything.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white mb-6 uppercase tracking-wide">Key Capabilities</h3>
              <div className="space-y-3">
                {[
                  'Adversarial attack simulation (FGSM, PGD, C&W)',
                  'Statistical consistency validation across 10K+ scenarios',
                  'Edge case generation using genetic algorithms',
                  'Real-time performance degradation analysis',
                  'Compliance reporting (ISO 42001, EU AI Act)'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#00E600] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-[#B3B3B3]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#0D0D0D] to-[#1A1A1A] border border-[#00E600] rounded-lg p-8">
            <h3 className="text-2xl font-bold text-white mb-6 uppercase tracking-wide">Proven Impact</h3>
            <div className="grid md:grid-cols-4 gap-6">
              <div>
                <div className="text-3xl font-bold text-[#00E600] mb-2">94%</div>
                <p className="text-sm text-[#B3B3B3]">Critical bugs detected pre-deployment</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#00E600] mb-2">10K+</div>
                <p className="text-sm text-[#B3B3B3]">Test scenarios per model</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#00E600] mb-2">$50M+</div>
                <p className="text-sm text-[#B3B3B3]">Production failures prevented</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#00E600] mb-2">99.7%</div>
                <p className="text-sm text-[#B3B3B3]">Model reliability post-validation</p>
              </div>
            </div>
          </div>
        </section>

        {/* Procurement Intelligence Page */}
        <section className="min-h-screen py-24 px-12 page-break">
          <div className="relative h-64 mb-12 rounded-lg overflow-hidden">
            <div className="absolute inset-0 opacity-30" style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1581090464777-f3220bbe1b8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlfGVufDB8fHxibHVlfDE3NjEwNjM5NzN8MA&ixlib=rb-4.1.0&q=85)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}></div>
            <div className="relative z-10 h-full flex items-center px-8">
              <div>
                <h2 className="text-5xl font-bold text-white mb-4 uppercase tracking-wide">
                  Procurement <span className="text-[#00E600]">Intelligence</span>
                </h2>
                <p className="text-xl text-[#B3B3B3]">AI-Powered Spend Optimization & Risk Management</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-12">
            <div>
              <h3 className="text-2xl font-bold text-white mb-6 uppercase tracking-wide">The Problem</h3>
              <p className="text-[#B3B3B3] mb-4">
                Enterprises overpay 15-30% on procurement due to poor visibility, maverick spending, and weak 
                negotiation leverage. Traditional procurement teams can't process the data volume needed to 
                identify hidden savings across 1,000+ suppliers and 100K+ transactions.
              </p>
              <p className="text-[#00E600] font-semibold">
                → Our AI analyzes 50M+ price data points in real-time, identifying $2M+ in savings opportunities 
                that human procurement teams miss. Predictive alerts 60-90 days before supply disruptions.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white mb-6 uppercase tracking-wide">Key Capabilities</h3>
              <div className="space-y-3">
                {[
                  'Cost optimization AI analyzing 50M+ data points',
                  'Supplier risk scoring & concentration analysis',
                  'Predictive demand forecasting (ML-based)',
                  'Contract intelligence & clause optimization',
                  'Automated RFP generation & vendor matching'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#00E600] rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-[#B3B3B3]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#0D0D0D] to-[#1A1A1A] border border-[#00E600] rounded-lg p-8">
            <h3 className="text-2xl font-bold text-white mb-6 uppercase tracking-wide">Proven Impact</h3>
            <div className="grid md:grid-cols-4 gap-6">
              <div>
                <div className="text-3xl font-bold text-[#00E600] mb-2">18%</div>
                <p className="text-sm text-[#B3B3B3]">Average cost reduction achieved</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#00E600] mb-2">40%</div>
                <p className="text-sm text-[#B3B3B3]">Faster procurement cycles</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#00E600] mb-2">85%</div>
                <p className="text-sm text-[#B3B3B3]">Supply risk events prevented</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#00E600] mb-2">60-90</div>
                <p className="text-sm text-[#B3B3B3]">Days early warning for disruptions</p>
              </div>
            </div>
          </div>
        </section>

        {/* Utility Network Intelligence Page */}
        <section className="min-h-screen py-24 px-12 page-break">
          <div className="relative h-64 mb-12 rounded-lg overflow-hidden">
            <div className="absolute inset-0 opacity-30" style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1597733336794-12d05021d510?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHx0ZWNobm9sb2d5fGVufDB8fHxibHVlfDE3NjEwNjM5Nzh8MA&ixlib=rb-4.1.0&q=85)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}></div>
            <div className="relative z-10 h-full flex items-center px-8">
              <div>
                <h2 className="text-5xl font-bold text-white mb-4 uppercase tracking-wide">
                  Utility Network <span className="text-[#00E600]">Intelligence</span>
                </h2>
                <p className="text-xl text-[#B3B3B3]">SimNet™ Suite - AI-Enhanced Infrastructure Simulation</p>
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-6 uppercase tracking-wide">SimNet™ Software Portfolio</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { name: 'SimNet SSGas™', desc: 'Steady-state gas network simulation (Newton-Raphson, Weymouth equations, 10K+ nodes)', metric: '99.97% accuracy' },
                { name: 'SimNet TSGas™', desc: 'Transient gas flow dynamics (PDE/MOC, surge analysis, millisecond resolution)', metric: 'Real-time SCADA' },
                { name: 'SimNet SSWater™', desc: 'Hydraulic network analysis (Hazen-Williams, Darcy-Weisbach, EPA EPANET compatible)', metric: '50K+ nodes' },
                { name: 'SimNet TSWater™', desc: 'Extended period simulation (FEA, 24-168hr periods, ML demand prediction)', metric: '30% energy savings' },
                { name: 'SimNet SSSewage™', desc: 'Pressurized sewerage networks (STEP systems, grinder pumps, EN 1091 compliant)', metric: 'Zero-downtime' },
                { name: 'SimNet SSHeat™', desc: 'District heating networks (thermal-hydraulic, CHP integration, 4GDH support)', metric: '25% heat loss reduction' }
              ].map((sw, idx) => (
                <div key={idx} className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="text-lg font-bold text-white">{sw.name}</h4>
                    <span className="text-[#00E600] text-sm font-semibold">{sw.metric}</span>
                  </div>
                  <p className="text-sm text-[#B3B3B3]">{sw.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#0D0D0D] to-[#1A1A1A] border border-[#00E600] rounded-lg p-8">
            <h3 className="text-2xl font-bold text-white mb-6 uppercase tracking-wide">Proven Impact</h3>
            <div className="grid md:grid-cols-4 gap-6">
              <div>
                <div className="text-3xl font-bold text-[#00E600] mb-2">35%</div>
                <p className="text-sm text-[#B3B3B3]">Non-Revenue Water (NRW) reduction</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#00E600] mb-2">48hr</div>
                <p className="text-sm text-[#B3B3B3]">Leak detection before visible evidence</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#00E600] mb-2">50M+</div>
                <p className="text-sm text-[#B3B3B3]">Citizens served by our systems</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-[#00E600] mb-2">99.8%</div>
                <p className="text-sm text-[#B3B3B3]">Infrastructure uptime guarantee</p>
              </div>
            </div>
          </div>
        </section>

        {/* Project Management & Digital Transformation */}
        <section className="min-h-screen py-24 px-12 page-break">
          <div className="mb-16">
            <h2 className="text-5xl font-bold text-white mb-4 uppercase tracking-wide">
              Enterprise <span className="text-[#00E600]">Operations</span>
            </h2>
            <div className="h-1 w-32 bg-[#00E600]"></div>
          </div>

          {/* Project Management */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-white mb-8 uppercase tracking-wide">Project Management SaaS</h3>
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <p className="text-[#B3B3B3] mb-4">
                  70% of enterprise projects fail. Our AI analyzes 10,000+ variables in real-time, predicting delays 
                  4-6 weeks before they materialize, auto-balancing workloads, and eliminating communication chaos.
                </p>
                <p className="text-[#00E600] font-semibold">
                  → $2B+ in projects delivered on time and under budget. When failure isn't an option, choose NYAXO.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-4">
                  <div className="text-2xl font-bold text-[#00E600] mb-1">94%</div>
                  <p className="text-xs text-[#B3B3B3]">On-time delivery rate</p>
                </div>
                <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-4">
                  <div className="text-2xl font-bold text-[#00E600] mb-1">3x</div>
                  <p className="text-xs text-[#B3B3B3]">Faster completion speed</p>
                </div>
                <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-4">
                  <div className="text-2xl font-bold text-[#00E600] mb-1">45%</div>
                  <p className="text-xs text-[#B3B3B3]">Resource efficiency gain</p>
                </div>
                <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-4">
                  <div className="text-2xl font-bold text-[#00E600] mb-1">4-6wk</div>
                  <p className="text-xs text-[#B3B3B3]">Early risk prediction</p>
                </div>
              </div>
            </div>
          </div>

          {/* Digital Transformation */}
          <div>
            <h3 className="text-3xl font-bold text-white mb-8 uppercase tracking-wide">Digital Transformation Consulting</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="text-[#B3B3B3] mb-4">
                  70% of transformations fail. We architect intelligent enterprises, not just digitize broken processes. 
                  Our AI-driven approach eliminates technical debt, identifies hidden opportunities, and delivers 
                  measurable ROI in months, not years.
                </p>
                <p className="text-[#00E600] font-semibold">
                  → $500M+ in transformation value created across Global 2000 enterprises. From legacy to AI-first.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-4">
                  <div className="text-2xl font-bold text-[#00E600] mb-1">89%</div>
                  <p className="text-xs text-[#B3B3B3]">Transformation success rate</p>
                </div>
                <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-4">
                  <div className="text-2xl font-bold text-[#00E600] mb-1">250%</div>
                  <p className="text-xs text-[#B3B3B3]">Average ROI achieved</p>
                </div>
                <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-4">
                  <div className="text-2xl font-bold text-[#00E600] mb-1">6mo</div>
                  <p className="text-xs text-[#B3B3B3]">Time to measurable value</p>
                </div>
                <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-4">
                  <div className="text-2xl font-bold text-[#00E600] mb-1">$500M+</div>
                  <p className="text-xs text-[#B3B3B3]">Value created</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Back Cover - Contact */}
        <section className="min-h-screen flex items-center justify-center px-12 page-break pt-32">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-12">
              <div className="flex items-center justify-center mb-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_find-your-agent/artifacts/m94nk0ss_NYAXO.png" 
                  alt="NYAXO Logo" 
                  className="h-24"
                />
              </div>
              <h2 className="text-5xl font-bold text-white mb-4 uppercase tracking-wide">
                Ready to Transform?
              </h2>
            </div>

            <div className="mb-12 space-y-4">
              <p className="text-xl text-[#B3B3B3]">
                Let's discuss how NYAXO can engineer your competitive advantage.
              </p>
            </div>

            <div className="bg-[#1A1A1A] border border-[#00E600] rounded-lg p-8 mb-12">
              <h3 className="text-2xl font-bold text-white mb-6 uppercase tracking-wide">Contact Information</h3>
              <div className="space-y-4 text-[#B3B3B3]">
                <div>
                  <p className="text-sm uppercase tracking-wide text-[#00E600] mb-1">Global Headquarters</p>
                  <p className="text-lg">10 ANSON ROAD #33-10C</p>
                  <p className="text-lg">SINGAPORE 079903</p>
                </div>
                <div>
                  <p className="text-sm uppercase tracking-wide text-[#00E600] mb-1">Email</p>
                  <p className="text-lg">office@nyaxo.net</p>
                </div>
              </div>
            </div>

            <div className="space-y-4 text-[#B3B3B3]">
              <p className="text-lg font-semibold text-white uppercase tracking-wide">SINGAPORE • GLOBAL AI INNOVATION HUB</p>
              <p className="text-sm">© 2025 NYAXO. All rights reserved. | Engineering the Future of Intelligence</p>
            </div>
          </div>
        </section>

      </div>

      {/* Print Styles */}
      <style>{`
        @keyframes glow {
          from {
            filter: drop-shadow(0 0 10px rgba(0, 230, 0, 0.5)) drop-shadow(0 0 20px rgba(0, 230, 0, 0.3));
          }
          to {
            filter: drop-shadow(0 0 30px rgba(0, 230, 0, 0.8)) drop-shadow(0 0 60px rgba(0, 230, 0, 0.5));
          }
        }
        
        @media print {
          * {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          html, body {
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 0;
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
            background-color: #0D0D0D !important;
          }
          
          .page-break {
            page-break-after: always !important;
            break-after: page !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: flex-start !important;
            width: 210mm;
            height: 297mm;
            max-height: 297mm;
            overflow: hidden !important;
            box-sizing: border-box;
            padding: 0 !important;
          }
          
          .print\\:hidden {
            display: none !important;
          }
          
          /* Ensure all backgrounds and colors print */
          div, section, p, h1, h2, h3, h4, h5, h6 {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
          }
          
          /* Page size and margins */
          @page {
            size: A4 portrait;
            margin: 0;
          }
          
          /* Prevent orphans and widows */
          p, h1, h2, h3, h4, h5, h6 {
            orphans: 3;
            widows: 3;
            page-break-inside: avoid;
          }
        }
        
        @media screen {
          body {
            background-color: #0D0D0D;
          }
        }
      `}</style>
    </div>
  );
};

export default BrochurePage;
